import { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  DownloadSimple,
  MagnifyingGlass,
  Stack,
  Star,
  Trash,
  UserCircle,
  Clock,
  Rocket,
} from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";

export default function Showcase() {
  const { user, booted } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("new");

  const load = async (s = sort) => {
    setLoading(true);
    try {
      const { data } = await api.get("/gallery", { params: { sort: s, limit: 120 } });
      setItems(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && user !== false) load(sort);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, sort]);

  const filtered = useMemo(() => {
    if (!query.trim()) return items;
    const q = query.toLowerCase();
    return items.filter(
      (x) =>
        x.name?.toLowerCase().includes(q) ||
        x.author_name?.toLowerCase().includes(q) ||
        x.description?.toLowerCase().includes(q),
    );
  }, [items, query]);

  if (!booted) {
    return (
      <div className="flex items-center justify-center h-[60vh] text-[var(--fg-2)] font-mono text-xs tracking-widest">
        LOADING…
      </div>
    );
  }
  if (user === false || !user) return <Navigate to="/login" replace />;

  const downloadDecal = async (it) => {
    // Pre-flight UX: warn non-admins about the 5-credit charge before
    // we fire the API call. Admins are exempt server-side; we detect
    // that on the response and skip the confirm dialog for them by
    // checking `user.role`.
    const isAdmin = user?.role === "admin";
    if (!isAdmin) {
      const ok = window.confirm(
        `Download "${it.name}" for 5 credits?\n\n` +
          `Your remaining balance will be shown after the download completes.`,
      );
      if (!ok) return;
    }
    try {
      // The SVG is no longer included in the list payload — it's only
      // returned by this credit-charging endpoint. Closes the loophole
      // where free-trial users could publish then re-download
      // indefinitely without consuming credits.
      const res = await api.post(`/gallery/${it.id}/download`);
      const svg = res.data?.svg;
      if (!svg) {
        toast.error("Download missing payload — please contact support.");
        return;
      }
      const blob = new Blob([svg], { type: "image/svg+xml" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = (it.name || "decal").replace(/[^\w.-]+/g, "_") + ".svg";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);

      // Optimistically update local state with new download count.
      setItems((arr) =>
        arr.map((x) => (x.id === it.id ? { ...x, downloads: res.data.downloads } : x)),
      );

      // Friendly toast — confirm the deduction and remaining balance.
      if (res.data?.source === "admin_free") {
        toast.success("Downloaded (admin — no credit charge).");
      } else {
        const remain = res.data?.credits_remaining;
        toast.success(
          `Downloaded — 5 credits charged.${
            remain != null ? ` ${remain} credits remaining.` : ""
          }`,
        );
      }
    } catch (e) {
      const status = e.response?.status;
      if (status === 402) {
        // Out-of-credits — point the user at /pricing.
        toast.error(
          formatApiErrorDetail(e.response?.data?.detail) ||
            "You're out of credits. Pick a plan from Pricing or buy a credit pack.",
        );
      } else {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      }
    }
  };

  const unpublish = async (it) => {
    if (!it.is_owner) return;
    if (!window.confirm(`Unpublish "${it.name}"?`)) return;
    try {
      await api.delete(`/gallery/${it.id}`);
      setItems((arr) => arr.filter((x) => x.id !== it.id));
      toast.success("Unpublished");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="max-w-[1440px] mx-auto">
      {/* Header */}
      <div className="border-b border-[var(--border)]">
        <div className="px-6 md:px-10 py-10 md:py-14 grid md:grid-cols-[1.4fr_1fr] gap-6 md:items-end">
          <div>
            <div className="micro-label flex items-center gap-2">
              <Stack size={12} weight="bold" /> / Decal Showcase
            </div>
            <h1 className="font-display font-black text-4xl md:text-6xl tracking-tight mt-2 leading-[0.95]">
              Community-tuned
              <br />
              racing decals.
            </h1>
            <p className="text-[var(--fg-2)] mt-4 max-w-lg">
              Ready-to-upload vectors from fellow drivers. Browse, download, and
              publish your own from the History page.
            </p>
          </div>
          <div className="flex flex-col gap-3">
            <div className="hairline bg-white flex items-center px-4 h-12" data-testid="showcase-search">
              <MagnifyingGlass size={16} weight="bold" className="text-[var(--fg-2)] mr-3" />
              <input
                className="flex-1 bg-transparent outline-none font-mono text-sm"
                placeholder="Search by name, author…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                data-testid="input-showcase-search"
              />
            </div>
            <div className="flex items-center gap-0 border border-[var(--border)]">
              {[
                { id: "new", label: "Newest", icon: Clock },
                { id: "popular", label: "Most downloaded", icon: Rocket },
              ].map((s) => {
                const active = sort === s.id;
                return (
                  <button
                    key={s.id}
                    onClick={() => setSort(s.id)}
                    data-testid={`showcase-sort-${s.id}`}
                    className={`flex-1 h-11 flex items-center justify-center gap-2 micro-label border-r last:border-r-0 border-[var(--border)] transition-colors ${
                      active ? "bg-black text-white" : "bg-white hover:bg-[var(--bg-2)]"
                    }`}
                  >
                    <s.icon size={12} weight="bold" />
                    {s.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="py-20 text-center text-[var(--fg-2)]" data-testid="showcase-loading">
          Loading…
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState query={query} />
      ) : (
        <div
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px bg-[var(--border)] border-b border-[var(--border)]"
          data-testid="showcase-grid"
        >
          {filtered.map((it) => (
            <article
              key={it.id}
              className="bg-white group relative hover:z-10"
              data-testid={`showcase-card-${it.id}`}
            >
              <div className="checker aspect-square flex items-center justify-center p-4 relative">
                <img
                  src={`data:image/svg+xml;utf8,${encodeURIComponent(it.svg)}`}
                  alt={it.name}
                  className="max-h-full max-w-full object-contain m-auto block"
                />
                <span
                  className="absolute top-2 left-2 chip"
                  style={{
                    background: "#fff",
                    borderColor: "var(--border)",
                  }}
                >
                  {it.mode?.toUpperCase() || "SVG"}
                </span>
                {it.is_owner && (
                  <button
                    onClick={() => unpublish(it)}
                    className="absolute top-2 right-2 h-7 w-7 border border-[var(--border)] bg-white flex items-center justify-center hover:border-[var(--accent)] hover:text-[var(--accent)]"
                    data-testid={`btn-unpublish-${it.id}`}
                    aria-label="Unpublish"
                    title="Unpublish"
                  >
                    <Trash size={12} weight="bold" />
                  </button>
                )}
              </div>
              <div className="p-4 border-t border-[var(--border)] space-y-2">
                <div className="font-display font-bold text-sm truncate">{it.name}</div>
                <div className="flex items-center gap-2 text-[11px] font-mono text-[var(--fg-2)]">
                  <UserCircle size={12} weight="bold" />
                  <span className="truncate">{it.author_name}</span>
                </div>
                {it.description && (
                  <p className="text-xs text-[var(--fg-2)] line-clamp-2">{it.description}</p>
                )}
                <div className="flex items-center justify-between pt-2">
                  <div className="flex items-center gap-3 text-[10px] font-mono text-[var(--fg-2)] tracking-wider">
                    <span>{(it.size_bytes / 1024).toFixed(1)}KB</span>
                    <span className="flex items-center gap-1">
                      <Star size={10} weight="fill" /> {it.downloads || 0}
                    </span>
                  </div>
                  <button
                    onClick={() => downloadDecal(it)}
                    className="btn-accent px-3 py-2 text-[10px]"
                    data-testid={`btn-download-${it.id}`}
                    title={user?.role === "admin" ? "Admin — free download" : "5 credits"}
                  >
                    <DownloadSimple size={12} weight="bold" />
                    {user?.role === "admin" ? "SVG" : "SVG · 5 credits"}
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

function EmptyState({ query }) {
  return (
    <div className="py-20 text-center" data-testid="showcase-empty">
      <Stack size={40} weight="regular" className="mx-auto text-[var(--fg-2)] mb-4" />
      <div className="font-display font-bold text-xl">
        {query ? "No matches" : "No decals published yet"}
      </div>
      <p className="text-[var(--fg-2)] mt-2 max-w-md mx-auto">
        {query
          ? "Try a different name or author."
          : "Convert a decal, save it to your history, then hit 'Publish' to share with the community."}
      </p>
      {!query && (
        <Link to="/convert" className="btn-accent inline-flex items-center gap-2 mt-6">
          Start converting
        </Link>
      )}
    </div>
  );
}
